import React, { FC, FormEvent, MouseEvent } from "react";

import {
  useInput,
  useApisMembersAiChatRoomsCreate,
  useAiChatConversationRequest,
  useDropdown,
} from "~/hooks";

import { FloatingChatInitialFormHeadMessage } from "./FloatingChatInitialFormHeadMessage";

import { OptionType, SingleValueType } from "~/domains";

import {
  AiChatSelectableExpertRoles,
  AiChatSelectablePromptTemplates,
} from "~/constants/aiChatSettings";

import { LabelWithDropDownField, LabelWithTextArea } from "~/ui";
import { Form, FormSubmitButton } from "~/ui";

type PropsType = {
  onClose: (e: MouseEvent<HTMLButtonElement>) => void;
  setAiChatRoomId: (chatRoomId: string) => void;
  onDisplayChatRooms: (e: MouseEvent<HTMLButtonElement>) => void;
};

export const FloatingChatInitialForm: FC<PropsType> = ({
  onClose,
  setAiChatRoomId,
  onDisplayChatRooms,
}: PropsType) => {
  const { mutate: postChatRoomRequest, isLoading: isPostChatRoomSubmitting } =
    useApisMembersAiChatRoomsCreate();
  const { aiChatConversationRequest, isEmployeeSubmitting } =
    useAiChatConversationRequest();

  const [{ value, onChange }, setValue] = useInput("");

  const [
    {
      dropdownValue: selectedExportRole,
      onChange: onChangeExportRole,
      dropdownSelectableValue: selectableExportRoles,
    },
  ] = useDropdown({
    selectableValue: AiChatSelectableExpertRoles,
  });

  const [
    {
      dropdownValue: selectedPromptTemplate,
      onChange: onChangePromptTemplate,
      dropdownSelectableValue: selectablePromptTemplates,
    },
  ] = useDropdown({
    selectableValue: AiChatSelectablePromptTemplates,
  });

  const handleChangePromptTemplate = (
    newValue: SingleValueType<OptionType>,
  ) => {
    if (value && newValue) {
      const isConfirm = confirm("入力中の内容を上書きしてもよろしいですか?");
      if (!isConfirm) return;
    }

    onChangePromptTemplate(newValue);
    if (!newValue) return;

    const selectedTemplate = AiChatSelectablePromptTemplates.find(
      (template) => template.id === newValue.value,
    );
    setValue(selectedTemplate?.template || "");
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    postChatRoomRequest(
      {
        body: {
          aiChatRoomSetting: selectedExportRole
            ? {
                expertRoleName: selectedExportRole.label,
                aiSystemMessage: `あなたは${selectedExportRole.label}として振る舞ってください。`,
              }
            : undefined,
        },
      },
      {
        onSuccess: (postChatRoomResponse) => {
          aiChatConversationRequest({
            message: value,
            aiChatRoomId: postChatRoomResponse.aiChatRoom.id,
            onSuccess: () => {
              setAiChatRoomId(postChatRoomResponse.aiChatRoom.id);
            },
          });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <FloatingChatInitialFormHeadMessage
        onClose={onClose}
        onDisplayChatRooms={onDisplayChatRooms}
      />
      <Form className="space-y-6" onSubmit={handleSubmit}>
        <LabelWithDropDownField
          labelText="回答してもらう人物"
          name="exportRole"
          options={selectableExportRoles}
          value={selectedExportRole}
          onChange={onChangeExportRole}
        />
        <LabelWithDropDownField
          labelText="テンプレート選択"
          name="promptTemplate"
          options={selectablePromptTemplates}
          value={selectedPromptTemplate}
          onChange={handleChangePromptTemplate}
        />
        <LabelWithTextArea
          labelText="フリーワード"
          value={value}
          required
          rows={14}
          onChange={onChange}
          placeholder="質問してみましょう"
          name="freeWord"
        />
        <FormSubmitButton
          value="実行する"
          color="primary"
          className="w-full"
          isReadOnly={isPostChatRoomSubmitting || isEmployeeSubmitting}
        />
      </Form>
    </div>
  );
};
